import React, { useMemo, useState } from "react";
import "./EvaluationSheet.css";

const EvaluationSheet = ({
                             evaluation,
                             readOnly = false,
                             showComment = true,
                             value = {},
                             onChange,
                         }) => {
    const [localAnswers, setLocalAnswers] = useState({});
    const [localComment, setLocalComment] = useState("");

    const sections = useMemo(() => {
        return [...(evaluation?.sections || [])].sort(
            (a, b) => (a.position || 0) - (b.position || 0)
        );
    }, [evaluation]);

    const scaleOptions = useMemo(() => {
        return [...(evaluation?.scaleOptions || [])].sort(
            (a, b) => (a.position || 0) - (b.position || 0)
        );
    }, [evaluation]);

    const answers = value.answers || localAnswers;
    const comment = value.comment ?? localComment;

    const handleAnswerChange = (questionId, optionValue) => {
        const nextAnswers = {
            ...answers,
            [questionId]: optionValue,
        };

        if (onChange) {
            onChange({
                answers: nextAnswers,
                comment,
            });
        } else {
            setLocalAnswers(nextAnswers);
        }
    };

    const handleCommentChange = (e) => {
        const nextComment = e.target.value;

        if (onChange) {
            onChange({
                answers,
                comment: nextComment,
            });
        } else {
            setLocalComment(nextComment);
        }
    };

    const getQuestionKey = (question, sectionIndex, questionIndex) =>
        question.id || `${sectionIndex}_${questionIndex}`;

    const getRoleLabel = (role) => {
        switch (role) {
            case "MANAGER":
                return "Manager";
            case "COLLABORATOR":
                return "Collaborateur";
            case "RESPONSABLE_FORMATION":
                return "Responsable formation";
            case "ALL":
                return "Tous";
            default:
                return role || "—";
        }
    };

    if (!evaluation) {
        return (
            <div className="es-empty">
                Aucune évaluation à afficher.
            </div>
        );
    }

    return (
        <div className="es-wrapper">
            <div className="es-header">
                <div className="es-code">{evaluation.code || "—"}</div>
                <h3 className="es-title">{evaluation.name || "Fiche d'évaluation"}</h3>
                {evaluation.description ? (
                    <p className="es-description">{evaluation.description}</p>
                ) : null}
            </div>

            {sections.length === 0 ? (
                <div className="es-empty">Aucune section trouvée.</div>
            ) : (
                <div className="es-table-wrap">
                    <table className="es-table">
                        <thead>
                        <tr>
                            <th className="es-th-section"></th>
                            <th className="es-th-question">Questions</th>
                            {/*<th className="es-th-role">Rôle</th>*/}
                            {scaleOptions.map((option, index) => (
                                <th key={option.id || index} className="es-th-rate">
                                    {option.label}
                                </th>
                            ))}
                        </tr>
                        </thead>

                        <tbody>
                        {sections.map((section, sectionIndex) => {
                            const questions = [...(section.questions || [])].sort(
                                (a, b) => (a.position || 0) - (b.position || 0)
                            );

                            if (questions.length === 0) {
                                return (
                                    <tr key={section.id || sectionIndex}>
                                        <td className="es-section-cell">
                                            {section.title}
                                        </td>
                                        <td className="es-no-question" colSpan={2 + scaleOptions.length}>
                                            Aucune question dans cette section
                                        </td>
                                    </tr>
                                );
                            }

                            return questions.map((question, questionIndex) => {
                                const questionKey = getQuestionKey(
                                    question,
                                    sectionIndex,
                                    questionIndex
                                );
                                const selectedValue = answers[questionKey];

                                return (
                                    <tr key={questionKey}>
                                        {questionIndex === 0 && (
                                            <th
                                                className="es-section-cell"
                                                rowSpan={questions.length}
                                            >
                                                <div className="es-section-title">
                                                    {section.title}
                                                </div>
                                                {/*<div className="es-section-weight">*/}
                                                {/*    {section.weight ?? 0}%*/}
                                                {/*</div>*/}
                                            </th>
                                        )}

                                        <td className="es-question-cell">
                                            {question.label}
                                        </td>

                                        {/*<td className="es-role-cell">*/}
                                        {/*    {getRoleLabel(question.targetRole)}*/}
                                        {/*</td>*/}

                                        {question.questionType === "CHOICE" ? (
                                            <td className="es-choice-cell" colSpan={Math.max(scaleOptions.length, 1)}>
                                                {[...(question.options || [])]
                                                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                                                    .map((option) => (
                                                        <span key={option.id || option.value || option.position} className="es-choice-chip">
                                                            {option.label}
                                                        </span>
                                                    ))}
                                            </td>
                                        ) : (
                                            scaleOptions.map((option, optionIndex) => (
                                                <td
                                                    key={option.id || optionIndex}
                                                    className="es-radio-cell"
                                                >
                                                    <label className="es-radio-wrap">
                                                        <input
                                                            type="radio"
                                                            name={`question_${questionKey}`}
                                                            checked={selectedValue === option.value}
                                                            onChange={() =>
                                                                handleAnswerChange(questionKey, option.value)
                                                            }
                                                            disabled={readOnly}
                                                        />
                                                        <span className="es-radio-custom" />
                                                    </label>
                                                </td>
                                            ))
                                        )}
                                    </tr>
                                );
                            });
                        })}
                        </tbody>
                    </table>
                </div>
            )}

            {showComment && (
                <div className="es-comment-box">
                    <label className="es-comment-label">Commentaire libre</label>
                    <textarea
                        className="es-comment-textarea"
                        rows={4}
                        value={comment}
                        onChange={handleCommentChange}
                        disabled={readOnly}
                        placeholder="Ajouter un commentaire..."
                    />
                </div>
            )}
        </div>
    );
};

export default EvaluationSheet;
